'use client';

import { BadgeCount, Button } from '@lulalife/design-system';
import { ArrowLeft } from '@lulalife/design-system/icons/line';
import type { ReactNode } from 'react';
import { DSSurface } from './DSSurface';

type SlideoutContentHeaderProps = {
  title: ReactNode;
  /** Default titles a tab section (Heading/sm, 36 px tall); Small titles a group inside a card. */
  size?: 'default' | 'small';
  /** Shows the Back button, for a sub view such as New Work Order. */
  onBack?: () => void;
  /** Extra class on Back: `close-panel-btn` keeps it to the mobile layout, as the old heads did. */
  backClassName?: string;
  count?: number;
  countTestId?: string;
  /** The Right slot: the section's actions. */
  right?: ReactNode;
  /**
   * Default size only: hold the row at 36 for the Right slot's buttons. Off, the row is the title's
   * 24, for a header that never has buttons (Invoices 8245:3089).
   */
  buttonRow?: boolean;
  'data-testid'?: string;
};

const SIZE_CLASSES = {
  default: {
    row: 'min-h-[36px]',
    title: 'mobile-heading-sm-base md:desktop-heading-sm-base',
  },
  small: {
    row: '',
    title: 'mobile-body-md-strong md:desktop-body-md-strong',
  },
};

/**
 * Local copy of the Figma library's `Slideout / Content Header`, which npm 0.30.23 doesn't ship
 * and no ticket covers yet. Swap this one component when the DS publishes it. One row, left to
 * right: Back, Title, count Badge, then the Right slot.
 */
export const SlideoutContentHeader = ({
  title,
  size = 'default',
  onBack,
  backClassName,
  count,
  countTestId,
  right,
  buttonRow = true,
  'data-testid': testId,
}: SlideoutContentHeaderProps) => (
  <DSSurface
    className={[
      'flex w-full items-center justify-between gap-space-12',
      buttonRow ? SIZE_CLASSES[size].row : '',
    ].join(' ')}
    data-testid={testId}
  >
    <div className="flex min-w-0 items-center gap-space-12">
      {onBack ? (
        <Button
          type="button"
          variant="tertiary"
          iconOnly
          aria-label="Back"
          className={backClassName}
          onClick={onBack}
        >
          <ArrowLeft />
        </Button>
      ) : null}
      {/* biome-ignore lint/a11y/useSemanticElements: Bootstrap's unlayered h1-h6 rules would beat the DS type utilities. */}
      <div
        role="heading"
        aria-level={3}
        className={`text-section-title ${SIZE_CLASSES[size].title}`}
      >
        {title}
      </div>
      {count !== undefined ? (
        <BadgeCount count={count} variant="muted" data-testid={countTestId} />
      ) : null}
    </div>
    {right ? <div className="flex shrink-0 items-center gap-space-8">{right}</div> : null}
  </DSSurface>
);
